import { useCallback, useEffect, useState } from 'react';

import { shouldUseApi } from '../services/api.js';

/**
 * useApiData — unified loader for a single async fetcher.
 *
 * Behaviour:
 *   - Demo mode (?demo=true) or no API gate → never calls the fetcher,
 *     returns { data: null, loading: false, error: null } so the caller
 *     uses its seed fallback silently.
 *   - Live mode → calls `fetcher`, sets loading/error/data, exposes refetch.
 *
 * Dependencies array behaves like useEffect's. Pass `deps=[]` for mount-only.
 */
export function useApiData(fetcher, deps = []) {
  const [state, setState] = useState({
    data: null,
    loading: shouldUseApi(),
    error: null,
  });

  const run = useCallback(async () => {
    if (!shouldUseApi()) {
      setState({ data: null, loading: false, error: null });
      return null;
    }
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await fetcher();
      setState({ data, loading: false, error: null });
      return data;
    } catch (err) {
      setState({ data: null, loading: false, error: err });
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!shouldUseApi()) {
        setState({ data: null, loading: false, error: null });
        return;
      }
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const data = await fetcher();
        if (!cancelled) setState({ data, loading: false, error: null });
      } catch (err) {
        if (!cancelled) setState({ data: null, loading: false, error: err });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { ...state, refetch: run };
}
